import { useCallback, useEffect, useRef, useState, type FormEvent } from 'react';
import { useAuth } from '../../auth/AuthProvider';
import { useToast } from '../../ui/Toast';
import { PageHead } from '../../shell/PageHead';
import { Button } from '../../ui/Button';
import { Badge } from '../../ui/Badge';
import { Field } from '../../ui/Field';
import { Input } from '../../ui/Input';
import { Modal } from '../../ui/Modal';
import { Icon } from '../../ui/Icon';

interface CompanyItem {
  id: string;
  nome: string;
  slug: string;
  status: 'ativa' | 'suspensa' | 'excluindo';
  criada_em: string;
  files_count: number;
  apikey_ativa: string | null;
  gestor_email: string | null;
}

function credValidEmail(v: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.trim());
}

function slugify(nome: string): string {
  return nome
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-+|-+$)/g, '')
    .slice(0, 60);
}

export function Companies() {
  const { apiFetch } = useAuth();
  const toast = useToast();

  const [items, setItems] = useState<CompanyItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [openCreate, setOpenCreate] = useState(false);
  const [createNome, setCreateNome] = useState('');
  const [createSlug, setCreateSlug] = useState('');
  const [createBusy, setCreateBusy] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  // Acesso web opcional do gestor (ADR-0021)
  const [withGestor, setWithGestor] = useState(false);
  const [createGEmail, setCreateGEmail] = useState('');
  const [createGSenha, setCreateGSenha] = useState('');

  const [lastCreated, setLastCreated] = useState<{ nome: string; apikey: string } | null>(null);

  // Modal de credenciais (set/reset login do gestor)
  const [credCo, setCredCo] = useState<CompanyItem | null>(null);
  const [credEmail, setCredEmail] = useState('');
  const [credSenha, setCredSenha] = useState('');
  const [credBusy, setCredBusy] = useState(false);
  const [credError, setCredError] = useState<string | null>(null);

  // Guard síncrono contra duplo-clique (state é assíncrono — §1.5.4).
  const creatingRef = useRef(false);
  const credRef = useRef(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    const r = await apiFetch<{ items: CompanyItem[]; total: number }>('/v1/admin/companies/');
    setLoading(false);
    if (!r.ok || !r.data) {
      setError(r.error?.message ?? 'Falha ao carregar');
      return;
    }
    setItems(r.data.items);
  }, [apiFetch]);

  useEffect(() => {
    load();
  }, [load]);

  async function onCreate(e: FormEvent) {
    e.preventDefault();
    if (creatingRef.current) return;
    if (withGestor && (!credValidEmail(createGEmail) || createGSenha.length < 8)) {
      setCreateError('Para acesso web, informe um email válido e senha de ao menos 8 caracteres.');
      return;
    }
    creatingRef.current = true;
    setCreateBusy(true);
    setCreateError(null);
    const body: Record<string, unknown> = { nome: createNome, slug: createSlug };
    if (withGestor) body.gestor = { email: createGEmail, senha: createGSenha };
    const r = await apiFetch<{ ok: true; id: string; nome: string; slug: string; apikey: string }>(
      '/v1/admin/companies/',
      { method: 'POST', body }
    );
    creatingRef.current = false;
    setCreateBusy(false);
    if (!r.ok || !r.data) {
      setCreateError(r.error?.message ?? 'Falha ao criar');
      return;
    }
    setLastCreated({ nome: r.data.nome, apikey: r.data.apikey });
    setOpenCreate(false);
    setCreateNome('');
    setCreateSlug('');
    setWithGestor(false);
    setCreateGEmail('');
    setCreateGSenha('');
    toast('Empresa criada com sucesso', 'success');
    await load();
  }

  function openCredentials(c: CompanyItem) {
    setCredCo(c);
    setCredEmail(c.gestor_email ?? '');
    setCredSenha('');
    setCredError(null);
  }

  async function onSaveCredentials(e: FormEvent) {
    e.preventDefault();
    if (credRef.current || !credCo) return;
    if (!credValidEmail(credEmail) || credSenha.length < 8) {
      setCredError('Informe um email válido e senha de ao menos 8 caracteres.');
      return;
    }
    credRef.current = true;
    setCredBusy(true);
    setCredError(null);
    const r = await apiFetch<{ ok: true; email: string; created: boolean }>(
      `/v1/admin/companies/${credCo.id}/credentials`,
      { method: 'PUT', body: { email: credEmail, senha: credSenha } }
    );
    credRef.current = false;
    setCredBusy(false);
    if (!r.ok || !r.data) {
      setCredError(r.error?.message ?? 'Falha ao salvar credenciais');
      return;
    }
    toast(r.data.created ? 'Acesso web criado' : 'Acesso web atualizado', 'success');
    setCredCo(null);
    await load();
  }

  async function copyApikey(key: string) {
    try {
      await navigator.clipboard.writeText(key);
      toast('Apikey copiada', 'info');
    } catch {
      toast('Falha ao copiar', 'danger');
    }
  }

  return (
    <>
      <PageHead
        title="Empresas"
        desc="Cadastro de empresas-cliente e suas apikeys."
        actions={
          <>
            <Button variant="ghost" icon="refresh" size="sm" onClick={load}>
              Atualizar
            </Button>
            <Button
              variant="brand"
              icon="plus"
              size="md"
              onClick={() => {
                setOpenCreate(true);
                setCreateError(null);
              }}
            >
              Nova empresa
            </Button>
          </>
        }
      />

      {lastCreated && (
        <div
          style={{
            background: 'var(--success-bg)',
            border: '1px solid color-mix(in oklch, var(--success) 30%, transparent)',
            borderRadius: 'var(--r-md)',
            padding: '14px 18px',
            marginBottom: 18,
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            flexWrap: 'wrap'
          }}
        >
          <Icon name="check" size={18} style={{ color: 'var(--success)' }} />
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 13.5, fontWeight: 600 }}>
              Empresa "{lastCreated.nome}" criada
            </div>
            <div style={{ fontSize: 12.5, color: 'var(--text-2)', marginTop: 2 }}>
              Apikey (copie agora — esta é a única vez que aparece em destaque):
            </div>
            <div
              className="mono"
              style={{
                marginTop: 6,
                fontSize: 12,
                padding: '6px 9px',
                background: 'var(--bg-1)',
                border: '1px solid var(--border)',
                borderRadius: 'var(--r-sm)',
                wordBreak: 'break-all'
              }}
            >
              {lastCreated.apikey}
            </div>
          </div>
          <Button variant="default" icon="copy" size="sm" onClick={() => copyApikey(lastCreated.apikey)}>
            Copiar
          </Button>
          <Button variant="ghost" size="sm" onClick={() => setLastCreated(null)}>
            Dispensar
          </Button>
        </div>
      )}

      {loading && <div className="skeleton" style={{ height: 160 }} />}
      {error && (
        <div
          style={{
            background: 'var(--danger-bg)',
            color: 'var(--danger)',
            border: '1px solid color-mix(in oklch, var(--danger) 30%, transparent)',
            borderRadius: 'var(--r-sm)',
            padding: '10px 14px',
            fontSize: 13.5
          }}
        >
          {error}
        </div>
      )}

      {!loading && !error && items.length === 0 && (
        <div
          className="grid-bg"
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            textAlign: 'center',
            padding: '64px 24px',
            borderRadius: 'var(--r-lg)',
            border: '1px dashed var(--border)',
            gap: 12,
            minHeight: 280
          }}
        >
          <div
            style={{
              width: 48,
              height: 48,
              borderRadius: 'var(--r-md)',
              background: 'var(--bg-1)',
              border: '1px solid var(--border)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'var(--text-2)'
            }}
          >
            <Icon name="building" size={22} />
          </div>
          <div>
            <h3 style={{ fontSize: 15.5, marginBottom: 6 }}>Nenhuma empresa cadastrada</h3>
            <p style={{ fontSize: 13.5, color: 'var(--text-2)' }}>
              Cadastre a primeira empresa-cliente para gerar uma apikey.
            </p>
          </div>
        </div>
      )}

      {!loading && !error && items.length > 0 && (
        <div
          style={{
            background: 'var(--bg-1)',
            border: '1px solid var(--border)',
            borderRadius: 'var(--r-lg)',
            overflow: 'hidden'
          }}
        >
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13.5 }}>
            <thead>
              <tr style={{ background: 'var(--bg-2)', textAlign: 'left' }}>
                <th style={{ padding: '11px 16px', fontSize: 12, fontWeight: 600, color: 'var(--text-2)' }}>Nome</th>
                <th style={{ padding: '11px 16px', fontSize: 12, fontWeight: 600, color: 'var(--text-2)' }}>Slug</th>
                <th style={{ padding: '11px 16px', fontSize: 12, fontWeight: 600, color: 'var(--text-2)' }}>Status</th>
                <th style={{ padding: '11px 16px', fontSize: 12, fontWeight: 600, color: 'var(--text-2)' }}>Arquivos</th>
                <th style={{ padding: '11px 16px', fontSize: 12, fontWeight: 600, color: 'var(--text-2)' }}>Apikey</th>
                <th style={{ padding: '11px 16px', fontSize: 12, fontWeight: 600, color: 'var(--text-2)' }}>Acesso web</th>
                <th style={{ padding: '11px 16px', fontSize: 12, fontWeight: 600, color: 'var(--text-2)' }} />
              </tr>
            </thead>
            <tbody>
              {items.map((c) => (
                <tr key={c.id} style={{ borderTop: '1px solid var(--border-soft)' }}>
                  <td style={{ padding: '12px 16px', fontWeight: 500 }}>{c.nome}</td>
                  <td style={{ padding: '12px 16px' }} className="mono">{c.slug}</td>
                  <td style={{ padding: '12px 16px' }}>
                    <Badge color={c.status === 'ativa' ? 'success' : c.status === 'excluindo' ? 'danger' : 'warning'} dot>
                      {c.status}
                    </Badge>
                  </td>
                  <td style={{ padding: '12px 16px' }} className="tnum">{c.files_count}</td>
                  <td style={{ padding: '12px 16px' }}>
                    {c.apikey_ativa ? (
                      <span
                        className="mono"
                        style={{
                          fontSize: 11.5,
                          padding: '3px 7px',
                          background: 'var(--bg-2)',
                          border: '1px solid var(--border)',
                          borderRadius: 'var(--r-sm)',
                          color: 'var(--text-1)'
                        }}
                      >
                        {c.apikey_ativa.slice(0, 14)}…
                      </span>
                    ) : (
                      <span style={{ fontSize: 12, color: 'var(--text-3)' }}>sem apikey</span>
                    )}
                  </td>
                  <td style={{ padding: '12px 16px' }}>
                    {c.gestor_email ? (
                      <span className="mono" style={{ fontSize: 12, color: 'var(--text-1)' }}>
                        {c.gestor_email}
                      </span>
                    ) : (
                      <span style={{ fontSize: 12, color: 'var(--text-3)' }}>sem acesso</span>
                    )}
                  </td>
                  <td style={{ padding: '12px 16px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                    {c.status !== 'excluindo' && (
                      <Button
                        variant="ghost"
                        size="sm"
                        icon="key"
                        onClick={() => openCredentials(c)}
                        title={c.gestor_email ? 'Alterar acesso web' : 'Criar acesso web'}
                      >
                        Acesso
                      </Button>
                    )}
                    {c.apikey_ativa && (
                      <Button variant="ghost" size="sm" icon="copy" onClick={() => copyApikey(c.apikey_ativa!)}>
                        Copiar
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Modal
        open={openCreate}
        onClose={() => setOpenCreate(false)}
        title="Nova empresa"
        subtitle="Será gerada uma apikey ativa imediatamente."
        width={460}
        footer={
          <>
            <Button variant="ghost" onClick={() => setOpenCreate(false)} disabled={createBusy}>
              Cancelar
            </Button>
            <Button
              variant="brand"
              type="submit"
              disabled={createBusy || !createNome || !createSlug}
              onClick={onCreate as any}
            >
              {createBusy ? 'Criando…' : 'Criar empresa'}
            </Button>
          </>
        }
      >
        <form onSubmit={onCreate} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {createError && (
            <div
              style={{
                background: 'var(--danger-bg)',
                color: 'var(--danger)',
                border: '1px solid color-mix(in oklch, var(--danger) 30%, transparent)',
                borderRadius: 'var(--r-sm)',
                padding: '9px 12px',
                fontSize: 13
              }}
            >
              {createError}
            </div>
          )}
          <Field label="Nome da empresa" required>
            <Input
              name="company-nome"
              value={createNome}
              onChange={(e) => {
                setCreateNome(e.target.value);
                setCreateSlug(slugify(e.target.value));
              }}
              disabled={createBusy}
              placeholder="Acme Ltda"
              autoFocus
            />
          </Field>
          <Field label="Slug (kebab-case, único)" required hint="Usado em URLs internas">
            <Input
              name="company-slug"
              value={createSlug}
              onChange={(e) => setCreateSlug(e.target.value)}
              disabled={createBusy}
              mono
              placeholder="acme-ltda"
            />
          </Field>

          <div style={{ borderTop: '1px solid var(--border-soft)', paddingTop: 14 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 9, cursor: 'pointer', fontSize: 13 }}>
              <input
                type="checkbox"
                checked={withGestor}
                onChange={(e) => setWithGestor(e.target.checked)}
                disabled={createBusy}
              />
              <span style={{ fontWeight: 500 }}>Criar acesso web para o gestor da empresa</span>
            </label>
            {withGestor && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginTop: 12 }}>
                <Field label="Email do gestor" required>
                  <Input
                    type="email"
                    value={createGEmail}
                    onChange={(e) => setCreateGEmail(e.target.value)}
                    disabled={createBusy}
                    placeholder="gestor@acme.com"
                  />
                </Field>
                <Field label="Senha do gestor" required hint="Mínimo 8 caracteres. O gestor entra no painel com email + senha.">
                  <Input
                    type="password"
                    value={createGSenha}
                    onChange={(e) => setCreateGSenha(e.target.value)}
                    disabled={createBusy}
                    autoComplete="new-password"
                  />
                </Field>
              </div>
            )}
          </div>
        </form>
      </Modal>

      <Modal
        open={!!credCo}
        onClose={() => setCredCo(null)}
        title={credCo ? `Acesso web · ${credCo.nome}` : 'Acesso web'}
        subtitle={
          credCo?.gestor_email
            ? 'Atualize o email e/ou defina uma nova senha do gestor.'
            : 'Defina o login do gestor para acessar o painel.'
        }
        width={440}
        footer={
          <>
            <Button variant="ghost" onClick={() => setCredCo(null)} disabled={credBusy}>
              Cancelar
            </Button>
            <Button
              variant="brand"
              type="submit"
              icon="key"
              disabled={credBusy || !credEmail || !credSenha}
              onClick={onSaveCredentials as any}
            >
              {credBusy ? 'Salvando…' : 'Salvar acesso'}
            </Button>
          </>
        }
      >
        <form onSubmit={onSaveCredentials} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {credError && (
            <div
              style={{
                background: 'var(--danger-bg)',
                color: 'var(--danger)',
                border: '1px solid color-mix(in oklch, var(--danger) 30%, transparent)',
                borderRadius: 'var(--r-sm)',
                padding: '9px 12px',
                fontSize: 13
              }}
            >
              {credError}
            </div>
          )}
          <Field label="Email de acesso" required>
            <Input
              type="email"
              value={credEmail}
              onChange={(e) => setCredEmail(e.target.value)}
              disabled={credBusy}
              placeholder="gestor@empresa.com"
              autoFocus
            />
          </Field>
          <Field label="Nova senha" required hint="Mínimo 8 caracteres. Substitui a senha atual do gestor.">
            <Input
              type="password"
              value={credSenha}
              onChange={(e) => setCredSenha(e.target.value)}
              disabled={credBusy}
              autoComplete="new-password"
            />
          </Field>
        </form>
      </Modal>
    </>
  );
}
